import {
  ActionIcon,
  Badge,
  Button,
  Container,
  Group,
  Menu,
  Modal,
  Paper,
  Skeleton,
  Stack,
  Tabs,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconChevronLeft, IconDotsVertical, IconEdit, IconTrash, IconMessageCircle, IconTimeline, IconWallet, IconChartLine, IconSettings } from "@tabler/icons-react";
import { createFileRoute, Outlet, useNavigate, useMatchRoute } from "@tanstack/react-router";
import {
  useProject,
  useUpdateProject,
  useDeleteProject,
  type UpdateProjectData,
} from "@/hooks/useProjects";
import { AlertBanner } from "@/components/AlertBanner";

export const Route = createFileRoute("/projects/$projectId")({
  component: ProjectDetailPage,
});

function ProjectDetailPage() {
  const { projectId } = Route.useParams();
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  const { data, isLoading } = useProject(projectId);
  const updateProject = useUpdateProject();
  const deleteProject = useDeleteProject();
  const [editOpened, { open: openEdit, close: closeEdit }] = useDisclosure(false);
  const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);

  const project = data?.project;

  // Determine active tab based on current route
  // NEW SIMPLIFIED TAB STRUCTURE: Overview | Money | Progress | Journal | Messages
  const getActiveTab = () => {
    // Money tab consolidates budget/commitments/actuals/cost-dashboard
    if (matchRoute({ to: "/projects/$projectId/money" as any })) return "money";
    if (matchRoute({ to: "/projects/$projectId/budget" })) return "money";
    if (matchRoute({ to: "/projects/$projectId/commitments" as any })) return "money";
    if (matchRoute({ to: "/projects/$projectId/actuals" as any })) return "money";
    if (matchRoute({ to: "/projects/$projectId/cost-dashboard" as any })) return "money";
    if (matchRoute({ to: "/projects/$projectId/cost-codes" })) return "more";
    // Progress tab (formerly Daily Logs)
    if (matchRoute({ to: "/projects/$projectId/progress" as any })) return "progress";
    if (matchRoute({ to: "/projects/$projectId/daily-logs" })) return "progress";
    // Journal stays the same
    if (matchRoute({ to: "/projects/$projectId/journal" as any })) return "journal";
    // Messages tab (formerly Inbox)
    if (matchRoute({ to: "/projects/$projectId/messages" as any })) return "messages";
    if (matchRoute({ to: "/projects/$projectId/inbox" as any })) return "messages";
    return "overview";
  };

  const form = useForm<UpdateProjectData>({
    initialValues: {
      name: project?.name || "",
      address: project?.address || "",
      city: project?.city || "",
      state: project?.state || "",
      zip: project?.zip || "",
    },
  });

  // Update form when project data loads
  if (project && !isLoading) {
    const currentValues = form.values;
    if (currentValues.name !== project.name) {
      form.setValues({
        name: project.name,
        address: project.address || "",
        city: project.city || "",
        state: project.state || "",
        zip: project.zip || "",
      });
    }
  }

  const handleEdit = form.onSubmit(async (values) => {
    try {
      await updateProject.mutateAsync({ id: projectId, data: values });
      notifications.show({
        title: "Success",
        message: "Project updated successfully!",
        color: "green",
      });
      closeEdit();
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to update project",
        color: "red",
      });
    }
  });

  const handleDelete = async () => {
    try {
      await deleteProject.mutateAsync(projectId);
      notifications.show({
        title: "Success",
        message: "Project deleted successfully!",
        color: "green",
      });
      navigate({ to: "/projects" });
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to delete project",
        color: "red",
      });
    }
  };

  const handleArchive = async () => {
    try {
      await updateProject.mutateAsync({
        id: projectId,
        data: { status: "archived" },
      });
      notifications.show({
        title: "Success",
        message: "Project archived successfully!",
        color: "green",
      });
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to archive project",
        color: "red",
      });
    }
  };

  const handleBack = () => {
    navigate({ to: "/projects" });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "green";
      case "completed":
        return "blue";
      case "archived":
        return "gray";
      default:
        return "gray";
    }
  };

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Stack gap="lg">
          <Skeleton height={40} width={200} />
          <Skeleton height={300} radius="md" />
        </Stack>
      </Container>
    );
  }

  if (!project) {
    return (
      <Container size="xl" py="xl">
        <Paper p="xl" withBorder>
          <Stack gap="md" align="center">
            <Text size="lg" c="dimmed">
              Project not found
            </Text>
            <Button onClick={handleBack}>Back to Projects</Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Alert Banner */}
        <AlertBanner projectId={projectId} />

        <Group justify="space-between" align="center">
          <Button
            variant="subtle"
            leftSection={<IconChevronLeft size={16} />}
            onClick={handleBack}
          >
            Back to Projects
          </Button>

          <Menu shadow="md" width={200}>
            <Menu.Target>
              <ActionIcon variant="subtle" size="lg">
                <IconDotsVertical size={20} />
              </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item leftSection={<IconEdit size={16} />} onClick={openEdit}>
                Edit Project
              </Menu.Item>
              {project.status === "active" && (
                <Menu.Item onClick={handleArchive}>
                  Archive Project
                </Menu.Item>
              )}
              <Menu.Divider />
              <Menu.Item
                color="red"
                leftSection={<IconTrash size={16} />}
                onClick={openDelete}
              >
                Delete Project
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>

        <Paper p="xl" withBorder>
          <Stack gap="md">
            <Group justify="space-between" align="flex-start">
              <Title order={1}>{project.name}</Title>
              <Badge color={getStatusColor(project.status)} variant="light" size="lg">
                {project.status}
              </Badge>
            </Group>

            {/* SIMPLIFIED TAB STRUCTURE: 5 Main Tabs + More Menu */}
            <Tabs value={getActiveTab()} onChange={(value) => {
              if (value === "overview") {
                navigate({ to: "/projects/$projectId", params: { projectId } });
              } else if (value === "money") {
                navigate({ to: "/projects/$projectId/money", params: { projectId } } as any);
              } else if (value === "progress") {
                navigate({ to: "/projects/$projectId/daily-logs", params: { projectId } });
              } else if (value === "journal") {
                navigate({ to: "/projects/$projectId/journal", params: { projectId } } as any);
              } else if (value === "messages") {
                navigate({ to: "/projects/$projectId/inbox", params: { projectId } } as any);
              } else if (value === "more") {
                navigate({ to: "/projects/$projectId/cost-codes", params: { projectId } });
              }
            }}>
              <Tabs.List>
                <Tabs.Tab value="overview">Overview</Tabs.Tab>
                <Tabs.Tab value="money" leftSection={<IconWallet size={16} />}>Money</Tabs.Tab>
                <Tabs.Tab value="progress" leftSection={<IconChartLine size={16} />}>Progress</Tabs.Tab>
                <Tabs.Tab value="journal" leftSection={<IconTimeline size={16} />}>Journal</Tabs.Tab>
                <Tabs.Tab value="messages" leftSection={<IconMessageCircle size={16} />}>Messages</Tabs.Tab>
                <Tabs.Tab value="more" leftSection={<IconSettings size={16} />} c="dimmed">More</Tabs.Tab>
              </Tabs.List>
            </Tabs>
          </Stack>
        </Paper>

        <Outlet />
      </Stack>

      <Modal opened={editOpened} onClose={closeEdit} title="Edit Project" centered>
        <form onSubmit={handleEdit}>
          <Stack gap="md">
            <TextInput
              label="Project Name"
              placeholder="Enter project name"
              {...form.getInputProps("name")}
              required
            />

            <TextInput
              label="Address"
              placeholder="Enter street address"
              {...form.getInputProps("address")}
            />

            <Group grow>
              <TextInput
                label="City"
                placeholder="City"
                {...form.getInputProps("city")}
              />
              <TextInput
                label="State"
                placeholder="State"
                {...form.getInputProps("state")}
              />
            </Group>

            <TextInput
              label="ZIP Code"
              placeholder="ZIP code"
              {...form.getInputProps("zip")}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeEdit} disabled={updateProject.isPending}>
                Cancel
              </Button>
              <Button type="submit" loading={updateProject.isPending}>
                Save Changes
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={deleteOpened}
        onClose={closeDelete}
        title="Delete Project"
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete this project? This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeDelete} disabled={deleteProject.isPending}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDelete} loading={deleteProject.isPending}>
              Delete Project
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
